import type { Place } from '@elara/client-core/models/PlaceModel';

import type { BaseDetailCardLayoutProps, CardLayout } from './DetailCardLayout.js';

import { getPlaceIcon } from '@elara/client-core/lib/place-category';

import { useRefreshPlace } from '#lib/use-refresh-place.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { CardDescription, DetailCardLayout } from './DetailCardLayout.js';

export interface PlaceCardProps extends BaseDetailCardLayoutProps {
  place: Place;
  /** Labelled actions rendered as a row below the content (`lg` only) — see `DetailCardLayout`. */
  footerActions?: React.ReactNode;
  /** `'row'` lays the card out photo-left for a hover popover — see `DetailCardLayout`. */
  layout?: CardLayout;
}

export function PlaceCard({
  place,
  size = 'default',
  className,
  actions,
  footerActions,
  layout,
  href,
  onClick,
}: PlaceCardProps) {
  useRefreshPlace(place?.id);

  const displayTitle = place?.displayName;
  const address = place?.shortAddress;
  // Google's one-liner first: it is written to be a single sentence, where the AI description is prose
  // for the guide and only gets generated when someone opens one. Either is bounded by `line-clamp-2`,
  // so the fallback keeps a card readable for the places Google has no summary for.
  const description = place?.editorialSummary ?? place?.generated?.description;

  return (
    <DetailCardLayout
      images={place?.allImages?.length ? place.allImages : undefined}
      title={displayTitle}
      type="place"
      size={size}
      actions={actions}
      footerActions={footerActions}
      layout={layout}
      className={className}
      href={href}
      onClick={onClick}
    >
      {/* One wrapping row rather than a stack: these are short facts, and stacked they turned a card
          into a column of one-word lines. `min-w-0` on each item is what lets `truncate` work inside
          a flex row — without it an over-long address pushes the row wider instead of ellipsing. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {place?.primaryCategory && (
          <div className="flex min-w-0 items-center gap-1">
            <MaterialIcon icon={getPlaceIcon(place.type)} size={IconSizes.xs} />
            <span className="truncate capitalize">{place.primaryCategory}</span>
          </div>
        )}
        {place?.priceRangeLabel && (
          <div className="flex min-w-0 items-center gap-1">
            <MaterialIcon icon="payments" size={IconSizes.xs} />
            <span className="truncate">{place.priceRangeLabel}</span>
          </div>
        )}
        {address && (
          <div className="flex min-w-0 items-center gap-1">
            <MaterialIcon icon="location_on" size={IconSizes.xs} />
            <span className="truncate">{address}</span>
          </div>
        )}
      </div>

      <CardDescription size={size}>{description}</CardDescription>
    </DetailCardLayout>
  );
}
